
import React, { useState, useEffect } from 'react';
import { useApp } from '../App';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle2, Loader2, CreditCard, Lock, ChevronRight, ArrowLeft, Mail, TicketPercent, User, Sparkles, MapPin, Plus } from 'lucide-react';
import { ref, push, set, get, update, onValue } from 'firebase/database';
import { db } from '../firebase';

const CheckoutPage: React.FC = () => {
  const { cart, user, profile, clearCart } = useApp();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [paymentStep, setPaymentStep] = useState(false);
  const [shippingConfig, setShippingConfig] = useState({ threshold: 250, fee: 45 });
  
  // Kupon State
  const [couponCodeInput, setCouponCodeInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(location.state?.couponCode || '');
  const [discount, setDiscount] = useState(location.state?.discount || 0);
  const [couponError, setCouponError] = useState('');

  // Adres Seçimi State
  const [selectedAddressId, setSelectedAddressId] = useState<string | 'new'>('new');
  const [formData, setFormData] = useState({
    email: user?.email || '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    phone: '',
    country: 'Türkiye'
  });

  const [cardData, setCardData] = useState({
    holderName: '',
    cardNumber: '',
    expireDate: '',
    cvc: ''
  });

  useEffect(() => {
    onValue(ref(db, 'settings/shipping'), (snapshot) => {
      if (snapshot.exists()) setShippingConfig(snapshot.val());
    });
  }, []);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({ ...prev, email: user.email || '' }));
    }
  }, [user]);

  // Kayıtlı adres seçildiğinde form verilerini güncelle
  useEffect(() => {
    if (selectedAddressId !== 'new' && profile?.addresses?.[selectedAddressId]) {
      const addr = profile.addresses[selectedAddressId];
      setFormData({
        email: user?.email || '',
        firstName: addr.firstName,
        lastName: addr.lastName,
        address: addr.fullAddress,
        city: addr.city,
        phone: addr.phone,
        country: 'Türkiye'
      });
    }
  }, [selectedAddressId, profile, user]);

  const subtotal = cart.reduce((acc, item) => acc + (item.product.price * item.quantity), 0);
  const discountedSubtotal = Math.max(0, subtotal - discount);
  const shippingFee = discountedSubtotal >= shippingConfig.threshold ? 0 : shippingConfig.fee;
  const finalTotal = discountedSubtotal + shippingFee;

  const handleApplyCoupon = async () => {
    setCouponError('');
    if (!couponCodeInput) return;

    try {
      const couponsRef = ref(db, 'coupons');
      const snapshot = await get(couponsRef);
      if (snapshot.exists()) {
        const coupons = snapshot.val();
        const coupon = Object.values(coupons).find((c: any) => c.code === couponCodeInput.toUpperCase() && c.isActive) as any;

        if (coupon) {
          if (coupon.expiryDate && coupon.expiryDate < Date.now()) {
            setCouponError('Bu kuponun süresi dolmuş.');
            return;
          }
          if (coupon.isSingleUse && coupon.isUsed) {
            setCouponError('Bu kupon daha önce kullanılmış.');
            return;
          }

          let discountAmount = 0;
          if (coupon.type === 'percentage') {
            discountAmount = (subtotal * coupon.value) / 100;
          } else {
            discountAmount = coupon.value;
          }

          setDiscount(discountAmount);
          setAppliedCoupon(coupon.code);
          setCouponError('');
        } else {
          setCouponError('Geçersiz kupon kodu.');
        }
      }
    } catch (error) {
      setCouponError('Hata oluştu.');
    }
  };

  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cart.length === 0) return;
    setLoading(true);

    try {
      const orderRef = push(ref(db, 'orders'));
      const orderId = orderRef.key as string;
      const orderData = {
        id: orderId,
        userId: user?.uid || 'guest',
        customerInfo: { ...formData },
        items: cart,
        total: finalTotal,
        discount: discount,
        couponCode: appliedCoupon,
        status: 'pending',
        createdAt: Date.now(),
        paymentStatus: 'paid'
      };

      await set(orderRef, orderData);

      // Kupon kullanım sayısını güncelle
      if (appliedCoupon) {
        const couponsRef = ref(db, 'coupons');
        const snapshot = await get(couponsRef);
        if (snapshot.exists()) {
          const coupons = snapshot.val();
          const couponEntry = Object.entries(coupons).find(([_, c]: [string, any]) => c.code === appliedCoupon);
          if (couponEntry) {
            const [cKey, cVal]: [string, any] = couponEntry;
            await update(ref(db, `coupons/${cKey}`), {
              usageCount: (cVal.usageCount || 0) + 1,
              isUsed: cVal.isSingleUse ? true : false
            });
          }
        }
      }

      setSuccess(true);
      clearCart();
    } catch (error) {
      alert("Sipariş oluşturulurken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="container mx-auto px-4 py-32 text-center">
        <div className="w-24 h-24 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner border border-emerald-100 animate-bounce">
          <CheckCircle2 size={48} />
        </div>
        <h2 className="text-4xl font-serif font-bold text-stone-900 mb-4">Siparişiniz Alındı!</h2>
        <p className="text-stone-500 mb-10 max-w-sm mx-auto font-medium italic">Siparişiniz başarıyla kaydedildi. Alışverişiniz için teşekkür ederiz.</p>
        <Link to="/" className="inline-block bg-stone-900 text-white px-10 py-4 rounded-2xl font-bold hover:bg-amber-800 transition-all shadow-xl">
          Alışverişe Devam Et
        </Link>
      </div>
    );
  }

  const addresses = profile?.addresses ? Object.values(profile.addresses) : [];

  return (
    <div className="container mx-auto px-4 md:px-6 pb-20 max-w-7xl pt-10">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          {/* Üye Girişi Hatırlatması */}
          {!user && (
            <div className="bg-amber-50 p-6 rounded-[2rem] border border-amber-100 mb-10 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-amber-800 shadow-sm">
                  <User size={24} />
                </div>
                <div>
                  <p className="font-bold text-stone-900 text-sm">Zaten bir hesabınız var mı?</p>
                  <p className="text-xs text-stone-500">Hızlı sipariş ve puanlar için giriş yapın.</p>
                </div>
              </div>
              <Link to="/auth" className="text-xs font-black uppercase tracking-widest text-amber-800 hover:underline">Üye Girişi Yap</Link>
            </div>
          )}

          {!paymentStep ? (
            <div className="space-y-8 animate-in slide-in-from-left duration-500">
               <div className="flex items-center space-x-4 mb-4">
                  <div className="w-10 h-10 bg-stone-900 text-white rounded-2xl flex items-center justify-center font-bold">1</div>
                  <h2 className="text-3xl font-black text-stone-900 font-serif">Teslimat Adresi</h2>
               </div>

               {/* Kayıtlı Adresler */}
               {user && addresses.length > 0 && (
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    {addresses.map((addr: any) => (
                      <button 
                        key={addr.id}
                        onClick={() => setSelectedAddressId(addr.id)}
                        className={`p-6 border rounded-[2rem] text-left transition-all ${selectedAddressId === addr.id ? 'border-amber-800 bg-amber-50 shadow-md ring-2 ring-amber-500/20' : 'border-stone-100 bg-white hover:border-stone-200'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                           <span className="text-[10px] font-black text-amber-800 uppercase tracking-widest">{addr.title}</span>
                           <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selectedAddressId === addr.id ? 'border-amber-800' : 'border-stone-200'}`}>
                              {selectedAddressId === addr.id && <div className="w-2 h-2 bg-amber-800 rounded-full"></div>}
                           </div>
                        </div>
                        <p className="font-bold text-stone-900 text-sm">{addr.firstName} {addr.lastName}</p>
                        <p className="text-xs text-stone-500 mt-1 line-clamp-2">{addr.fullAddress}</p>
                        <p className="text-[10px] font-bold text-stone-400 mt-2 uppercase">{addr.district} / {addr.city}</p>
                      </button>
                    ))}
                    <button 
                      onClick={() => setSelectedAddressId('new')}
                      className={`p-6 border-2 border-dashed rounded-[2rem] flex flex-col items-center justify-center text-stone-400 hover:text-amber-800 hover:border-amber-500 transition-all ${selectedAddressId === 'new' ? 'border-amber-500 bg-amber-50/50' : 'border-stone-100 bg-stone-50/30'}`}
                    >
                      <Plus size={24} className="mb-2" />
                      <span className="text-xs font-bold uppercase tracking-widest">Yeni Adres Kullan</span>
                    </button>
                 </div>
               )}
               
               <form onSubmit={(e) => { e.preventDefault(); setPaymentStep(true); }} className="bg-white p-10 rounded-[3rem] border border-stone-100 shadow-sm space-y-6">
                 {selectedAddressId === 'new' ? (
                   <>
                     <div>
                        <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-2 ml-4">E-posta</label>
                        <div className="relative">
                          <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-300" size={18} />
                          <input type="email" required placeholder="ornek@mail.com" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 pl-14 pr-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold text-stone-900" />
                        </div>
                     </div>
                     <div className="grid grid-cols-2 gap-4">
                        <input type="text" required placeholder="Adınız" value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 outline-none font-bold text-stone-900" />
                        <input type="text" required placeholder="Soyadınız" value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 outline-none font-bold text-stone-900" />
                     </div>
                     <textarea required placeholder="Tam Adresiniz" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold resize-none text-stone-900" rows={3}></textarea>
                     <div className="grid grid-cols-2 gap-4">
                        <input type="text" required placeholder="Şehir" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 outline-none font-bold text-stone-900" />
                        <input type="tel" required placeholder="Telefon" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 outline-none font-bold text-stone-900" />
                     </div>
                   </>
                 ) : (
                   <div className="p-6 bg-stone-50 rounded-2xl border border-stone-100 space-y-2">
                      <p className="text-[10px] font-black text-amber-800 uppercase tracking-widest">Seçili Adres Detayı</p>
                      <p className="font-bold text-stone-900">{formData.firstName} {formData.lastName}</p>
                      <p className="text-xs text-stone-600 italic">{formData.address}</p>
                      <p className="text-xs font-bold text-stone-900">{formData.city} / {formData.phone}</p>
                   </div>
                 )}

                 {/* Kupon Alanı - Buton Üstünde */}
                 <div className="pt-4 border-t border-stone-50">
                    <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">İndirim Kuponunuz mu var?</p>
                    <div className="flex gap-2">
                       <div className="relative flex-1">
                          <TicketPercent className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-300" size={16} />
                          <input 
                            type="text" 
                            placeholder="KUPON KODU" 
                            value={couponCodeInput}
                            onChange={e => setCouponCodeInput(e.target.value.toUpperCase())}
                            className="w-full bg-stone-50 border border-stone-100 rounded-xl py-3 pl-10 pr-4 text-xs font-bold outline-none focus:ring-2 focus:ring-amber-500"
                          />
                       </div>
                       <button 
                        type="button"
                        onClick={handleApplyCoupon}
                        className="bg-stone-900 text-white px-6 py-3 rounded-xl text-xs font-bold hover:bg-amber-800 transition-all"
                       >
                        Uygula
                       </button>
                    </div>
                    {couponError && <p className="text-red-500 text-[10px] mt-2 font-bold ml-2">{couponError}</p>}
                    {appliedCoupon && (
                      <p className="text-emerald-600 text-[10px] mt-2 font-black uppercase tracking-widest flex items-center ml-2">
                        <CheckCircle2 size={12} className="mr-1" /> "{appliedCoupon}" İndirimi Uygulandı
                      </p>
                    )}
                 </div>

                 <button type="submit" className="w-full bg-stone-900 text-white py-5 rounded-2xl font-bold flex items-center justify-center space-x-3 hover:bg-amber-800 shadow-xl transition-all">
                    <span>Ödeme Bilgilerine Geç</span>
                    <ChevronRight size={22} />
                 </button>
               </form>
            </div>
          ) : (
            <div className="space-y-8 animate-in slide-in-from-right duration-500">
               <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-amber-800 text-white rounded-2xl flex items-center justify-center font-bold">2</div>
                    <h2 className="text-3xl font-black text-stone-900 font-serif">Ödeme Yöntemi</h2>
                  </div>
                  <button onClick={() => setPaymentStep(false)} className="text-xs font-black uppercase text-stone-400 flex items-center hover:text-stone-900">
                    <ArrowLeft size={16} className="mr-2" /> Adresi Düzenle
                  </button>
               </div>

               <form onSubmit={handlePlaceOrder} className="bg-white p-10 rounded-[3rem] border border-stone-100 shadow-sm space-y-6">
                 <div className="p-8 bg-stone-900 rounded-[2.5rem] text-white mb-8 relative overflow-hidden shadow-2xl">
                    <CreditCard className="mb-8 opacity-30" size={40} />
                    <div className="space-y-6">
                       <p className="text-2xl font-mono tracking-[0.25em]">{cardData.cardNumber || '**** **** **** ****'}</p>
                       <div className="flex justify-between items-end">
                          <div>
                            <p className="text-[9px] uppercase font-black text-stone-500 mb-1">KART SAHİBİ</p>
                            <p className="font-bold text-sm uppercase">{cardData.holderName || 'AD SOYAD'}</p>
                          </div>
                          <div className="text-right">
                             <p className="text-[9px] uppercase font-black text-stone-500 mb-1">SKT</p>
                             <p className="font-bold text-sm">{cardData.expireDate || 'AA/YY'}</p>
                          </div>
                       </div>
                    </div>
                 </div>

                 <div className="space-y-4">
                    <input type="text" required placeholder="KART ÜZERİNDEKİ İSİM" value={cardData.holderName} onChange={e => setCardData({...cardData, holderName: e.target.value.toUpperCase()})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 outline-none font-bold text-stone-900" />
                    <input type="text" maxLength={16} required placeholder="KART NUMARASI" value={cardData.cardNumber} onChange={e => setCardData({...cardData, cardNumber: e.target.value.replace(/\D/g, '')})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 outline-none font-bold text-stone-900" />
                    <div className="grid grid-cols-2 gap-4">
                       <input type="text" placeholder="AA/YY" required value={cardData.expireDate} onChange={e => setCardData({...cardData, expireDate: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 outline-none font-bold text-center text-stone-900" />
                       <input type="password" maxLength={3} required placeholder="CVC" value={cardData.cvc} onChange={e => setCardData({...cardData, cvc: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold text-center text-stone-900" />
                    </div>
                 </div>

                 <div className="pt-6 border-t border-stone-50 text-center">
                    <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-4">ÖDENECEK TOPLAM TUTAR</p>
                    <p className="text-4xl font-black text-amber-800 mb-8">₺{finalTotal.toLocaleString()}</p>
                    
                    <button type="submit" disabled={loading} className="w-full bg-emerald-600 text-white py-6 rounded-2xl font-bold flex items-center justify-center space-x-3 hover:bg-emerald-700 shadow-2xl disabled:opacity-50 text-xl transition-all">
                        {loading ? <Loader2 size={28} className="animate-spin" /> : (
                          <>
                            <Lock size={22} />
                            <span>Ödemeyi Tamamla</span>
                          </>
                        )}
                    </button>
                    <p className="mt-4 text-[10px] text-stone-400 font-medium italic">Tüm işlemler 256-bit SSL şifreleme ile güvence altındadır.</p>
                 </div>
               </form>
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          <div className="bg-white border border-stone-100 rounded-[3rem] p-10 shadow-sm sticky top-32 space-y-8">
            <h3 className="text-2xl font-bold text-stone-900 font-serif">Sipariş Özeti</h3>
            
            <div className="space-y-4 max-h-60 overflow-y-auto pr-2 scrollbar-hide">
              {cart.map((item, i) => (
                <div key={i} className="flex justify-between items-center text-sm">
                  <div className="flex items-center space-x-3">
                     <span className="w-6 h-6 bg-stone-100 text-[10px] font-black flex items-center justify-center rounded-lg">{item.quantity}</span>
                     <span className="text-stone-600 font-medium truncate w-32">{item.product.name}</span>
                  </div>
                  <span className="font-bold text-stone-900">₺{(item.product.price * item.quantity).toLocaleString()}</span>
                </div>
              ))}
            </div>

            <div className="pt-6 border-t border-stone-100 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-stone-400 font-medium">Ara Toplam</span>
                <span className="text-stone-600 font-bold">₺{subtotal.toLocaleString()}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sm text-emerald-600 font-black">
                  <span>İndirim ({appliedCoupon})</span>
                  <span>-₺{discount.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-stone-400 font-medium">Kargo</span>
                {shippingFee === 0 ? (
                  <span className="text-amber-700 font-bold uppercase tracking-widest text-[10px]">Ücretsiz</span>
                ) : (
                  <span className="text-stone-600 font-bold">₺{shippingFee.toLocaleString()}</span>
                )}
              </div>
              <div className="flex justify-between items-end pt-4">
                <span className="text-xl font-serif font-bold text-stone-900">Genel Toplam</span>
                <span className="text-3xl font-black text-amber-800">₺{finalTotal.toLocaleString()}</span>
              </div>
            </div>

            <div className="p-6 bg-amber-50 rounded-2xl flex items-center space-x-4">
               <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-amber-800 shadow-sm">
                  <Sparkles size={20} />
               </div>
               <p className="text-[10px] font-bold text-amber-900 uppercase leading-relaxed tracking-wider">Lumina Home Güvencesiyle Özenle Paketlenmiş El Yapımı Ürünler</p>
            </div>
          </div>
        </div>
      </div>
    </div> 
  );
};

export default CheckoutPage;
